import * as React from "react";
import { DatePicker, FormCheckbox, Input } from "@jhool-io/fe-components";
import { ConsentPageConfig } from "../../types/client-forms.types";
import ConsentSignatureInput from "./ConsentSignatureInput";

interface ConsentPageFieldsProps {
    page: ConsentPageConfig;
    currentPage: number;
    formType?: string | null;
    variant?: "adult" | "minor";
    initials: string;
    fullName: string;
    checkbox: boolean;
    multiCheckboxValues: Record<string, boolean>;
    date: Date;
    dateOfBirth: Date | null;
    custodianFullName: string;
    custodianDateOfBirth: Date | null;
    signature: string;
    additionalInitials: Record<string, string>;
    additionalFullNames: Record<string, string>;
    additionalSignatures: Record<string, string>;
    fieldError: string;
    onInitialsChange: (value: string) => void;
    onFullNameChange: (value: string) => void;
    onCheckboxChange: (value: boolean) => void;
    onMultiCheckboxChange: (key: string, value: boolean) => void;
    onDateChange: (value: Date) => void;
    onDateOfBirthChange: (value: Date) => void;
    onCustodianFullNameChange: (value: string) => void;
    onCustodianDateOfBirthChange: (value: Date) => void;
    onSignatureChange: (value: string) => void;
    onAdditionalInitialsChange: (key: string, value: string) => void;
    onAdditionalFullNameChange: (key: string, value: string) => void;
    onAdditionalSignatureChange: (key: string, value: string) => void;
    onInteract: () => void;
}

export default function ConsentPageFields({
    page,
    currentPage,
    formType,
    variant,
    initials,
    fullName,
    checkbox,
    multiCheckboxValues,
    date,
    dateOfBirth,
    custodianFullName,
    custodianDateOfBirth,
    signature,
    additionalInitials,
    additionalFullNames,
    additionalSignatures,
    fieldError,
    onInitialsChange,
    onFullNameChange,
    onCheckboxChange,
    onMultiCheckboxChange,
    onDateChange,
    onDateOfBirthChange,
    onCustodianFullNameChange,
    onCustodianDateOfBirthChange,
    onSignatureChange,
    onAdditionalInitialsChange,
    onAdditionalFullNameChange,
    onAdditionalSignatureChange,
    onInteract,
}: ConsentPageFieldsProps) {
    const hideSignatureModeToggle =
        variant === "minor" || formType === "chp_testing_consent_form";
    const hasFields =
        page.requiresInitials ||
        Boolean(page.additionalInitialsFields?.length) ||
        page.requireFullName ||
        Boolean(page.additionalFullNameFields?.length) ||
        page.requiresCustodianFullName ||
        page.requiresCustodianDateOfBirth ||
        page.requiresSignature ||
        Boolean(page.additionalSignatureFields?.length) ||
        page.requiresCheckbox ||
        page.requiresMultiCheckbox ||
        page.requiresDate ||
        page.requiresDateOfBirth;

    if (!hasFields) {
        return null;
    }

    return (
        <div className="border-t border-strokelight pt-12 space-y-12">
            {page.requiresInitials && (
                <Input
                    id={`initials-${currentPage}`}
                    label={page.initialsLabel ?? "Initials"}
                    type="text"
                    value={initials}
                    onChange={(event) => onInitialsChange(event.target.value)}
                    maxLength={10}
                    placeholder="e.g. J.D."
                />
            )}

            {page.additionalInitialsFields?.map((field) => (
                <Input
                    key={field.key}
                    id={`initials-${field.key}`}
                    label={field.label}
                    type="text"
                    value={additionalInitials[field.key] ?? ""}
                    onChange={(event) =>
                        onAdditionalInitialsChange(
                            field.key,
                            event.target.value
                        )
                    }
                    maxLength={10}
                    placeholder="e.g. J.D."
                />
            ))}

            {page.requireFullName && (
                <Input
                    id={`full_name-${currentPage}`}
                    label={
                        page.fullNameLabel ?? "Client Name (Full Legal Name)"
                    }
                    type="text"
                    value={fullName}
                    onChange={(event) => onFullNameChange(event.target.value)}
                    maxLength={150}
                    placeholder="e.g. John Doe"
                />
            )}

            {page.additionalFullNameFields?.map((field) => (
                <Input
                    key={field.key}
                    id={`full_name-${field.key}`}
                    label={field.label}
                    type="text"
                    value={additionalFullNames[field.key] ?? ""}
                    onChange={(event) =>
                        onAdditionalFullNameChange(
                            field.key,
                            event.target.value
                        )
                    }
                    maxLength={150}
                    placeholder="e.g. John Doe"
                />
            ))}

            {page.requiresCustodianFullName && (
                <Input
                    id={`custodian_full_name-${currentPage}`}
                    label={
                        page.custodianFullNameLabel ??
                        "Legal Custodian Full Legal Name"
                    }
                    type="text"
                    value={custodianFullName}
                    onChange={(event) =>
                        onCustodianFullNameChange(event.target.value)
                    }
                    maxLength={150}
                    placeholder="e.g. John Doe"
                />
            )}

            {page.requiresSignature && (
                <ConsentSignatureInput
                    value={signature}
                    onChange={onSignatureChange}
                    onInteract={onInteract}
                    hideModeToggle={hideSignatureModeToggle}
                    label={page.signatureLabel}
                />
            )}

            {page.additionalSignatureFields?.map((field) => (
                <ConsentSignatureInput
                    key={field.key}
                    value={additionalSignatures[field.key] ?? ""}
                    onChange={(value) =>
                        onAdditionalSignatureChange(field.key, value)
                    }
                    onInteract={onInteract}
                    hideModeToggle={hideSignatureModeToggle}
                    label={field.label}
                />
            ))}

            {page.requiresDate && (
                <DatePicker
                    label="Date"
                    selected={date}
                    onChange={(value) => onDateChange(value ?? new Date())}
                />
            )}

            {page.requiresDateOfBirth && (
                <DatePicker
                    label="Date of Birth"
                    selected={dateOfBirth}
                    onChange={(value) =>
                        onDateOfBirthChange(value ?? new Date())
                    }
                />
            )}

            {page.requiresCustodianDateOfBirth && (
                <DatePicker
                    label={
                        page.custodianDateOfBirthLabel ??
                        "Legal Custodian Date of Birth"
                    }
                    selected={custodianDateOfBirth}
                    onChange={(value) =>
                        onCustodianDateOfBirthChange(value ?? new Date())
                    }
                />
            )}

            {page.requiresCheckbox && (
                <FormCheckbox
                    checked={checkbox}
                    onChange={(event) => onCheckboxChange(event.target.checked)}
                    label={
                        page.checkboxLabel ??
                        "I acknowledge and agree to the above."
                    }
                />
            )}

            {page.requiresMultiCheckbox &&
                page.multiCheckboxOptions &&
                page.multiCheckboxOptions.length > 0 && (
                    <div className="space-y-8">
                        <p className="text-xs uppercase tracking-wide text-[rgba(11,19,43,0.4)]">
                            Please check all that apply
                        </p>
                        {page.multiCheckboxOptions.map((option) => (
                            <FormCheckbox
                                key={option.key}
                                checked={
                                    multiCheckboxValues[option.key] ?? false
                                }
                                onChange={(event) =>
                                    onMultiCheckboxChange(
                                        option.key,
                                        event.target.checked
                                    )
                                }
                                label={option.label}
                            />
                        ))}
                    </div>
                )}

            {fieldError && <p className="text-xs text-red-500">{fieldError}</p>}
        </div>
    );
}
